import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Users, ShieldCheck, AlertTriangle, AlertOctagon, Plus, Activity as ActivityIcon, Sparkles, ClipboardCheck, ScanLine, HardHat, ChevronRight, IdCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatCard, PageHeader, EmptyState, PageLoading, fmtDate, fmtDateTime } from "@/components/shared";
import { ComplianceNewsList } from "@/components/ComplianceNewsWidget";
import { Newspaper } from "lucide-react";
import api from "@/lib/api";

function ScoreRing({ score, label }) {
  const r = 52, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  const color = score >= 80 ? "#059669" : score >= 50 ? "#D97706" : "#DC2626";
  return (
    <div className="relative flex h-40 w-40 items-center justify-center">
      <svg width="140" height="140" className="-rotate-90">
        <circle cx="70" cy="70" r={r} fill="none" stroke="#E2E8F0" strokeWidth="12" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <div className="absolute text-center">
        <div className="font-heading text-4xl font-extrabold text-navy">{score}%</div>
        <div className="overline text-slate-400">{label}</div>
      </div>
    </div>
  );
}

function PortalBar({ label, b, t, testid }) {
  const total = b.total || 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <div data-testid={testid}>
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-sm font-semibold text-navy">{label}</span>
        <span className="text-xs text-slate-400">{b.active}/{total} {t("ovw.portalActiveShort")}</span>
      </div>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">
        <div className="bg-emerald-500" style={{ width: `${pct(b.active)}%`, transition: "width 0.6s ease" }} />
        <div className="bg-amber-400" style={{ width: `${pct(b.invited)}%`, transition: "width 0.6s ease" }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
        <span className="inline-flex items-center gap-1 text-slate-500"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {b.active} {t("ovw.portalActiveL")}</span>
        <span className="inline-flex items-center gap-1 text-slate-500"><span className="h-2 w-2 rounded-full bg-amber-400" /> {b.invited} {t("ovw.portalInvited")}</span>
        <span className="inline-flex items-center gap-1 text-slate-500"><span className="h-2 w-2 rounded-full bg-slate-300" /> {b.not_invited} {t("ovw.portalNotInvited")}</span>
      </div>
    </div>
  );
}


export default function Overview() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [subcon, setSubcon] = useState(null);
  const [portal, setPortal] = useState(null);
  const [onboard, setOnboard] = useState(false);
  const [busy, setBusy] = useState(false);
  const reload = () => api.get("/employer/overview").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    api.get("/employer/entitlements").then((r) => {
      const acc = r.data.entitlements?.SUBCONTRACTOR_COMPLIANCE?.access;
      if (acc === "full" || acc === "read_only") {
        api.get("/subcon/alerts").then((a) => setSubcon(a.data.counts || {})).catch(() => {});
      }
    }).catch(() => {});
  }, []);
  useEffect(() => { api.get("/employer/portal-stats").then((r) => setPortal(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (data && !localStorage.getItem("eg_onboarded")) setOnboard(true); }, [data]);
  const closeOnboard = () => { localStorage.setItem("eg_onboarded", "1"); setOnboard(false); };
  const loadDemo = async () => {
    setBusy(true);
    try { await api.post("/employer/init-demo"); toast.success(t("x.demoDone")); closeOnboard(); reload(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const addBtn = (testid) => (
    <Link to="/app/employer/employees/new"><Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid={testid}><Plus className="mr-1 h-4 w-4" /> {t("ovw.add")}</Button></Link>
  );

  const OnboardModal = () => (
    <Dialog open={onboard} onOpenChange={(o) => !o && closeOnboard()}>
      <DialogContent data-testid="onboarding-modal">
        <DialogHeader><DialogTitle>{t("x.onboardTitle")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{t("x.onboardSub")}</p>
        <div className="mt-2 space-y-3">
          {[[Users, t("x.onboardStep1")], [ClipboardCheck, t("x.onboardStep2")], [ScanLine, t("x.onboardStep3")]].map(([Icon, txt], i) => (
            <div key={i} className="flex items-start gap-3 text-sm text-navy">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cyan/10 text-cyan-dark"><Icon className="h-4 w-4" /></div>
              <span className="text-slate-600">{txt}</span>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={closeOnboard} data-testid="onboard-skip-btn">{t("x.onboardSkip")}</Button>
          <Button variant="outline" onClick={() => { closeOnboard(); setTimeout(() => window.dispatchEvent(new Event("eg-start-tour")), 300); }} data-testid="onboard-tour-btn" className="rounded-full">{t("tour.help")}</Button>
          <Button onClick={loadDemo} disabled={busy} data-testid="onboard-demo-btn" className="rounded-full bg-cyan text-white hover:bg-cyan-dark"><Sparkles className="mr-1 h-4 w-4" /> {t("x.onboardDemo")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (data === null) return <PageLoading />;
  if (data && data.total === 0)
    return (
      <>
        <OnboardModal />
        <PageHeader title={t("ovw.title")} subtitle={t("ovw.subtitle")} />
        <EmptyState icon={Users} title={t("ovw.emptyTitle")} testid="overview-empty"
          subtitle={t("ovw.emptySub")} action={
            <div className="flex flex-wrap justify-center gap-2">
              {addBtn("empty-add-employee")}
              <Button variant="outline" className="rounded-full" onClick={loadDemo} disabled={busy} data-testid="empty-demo-btn"><Sparkles className="mr-1 h-4 w-4" /> {t("x.demoData")}</Button>
            </div>
          } />
      </>
    );

  const { counts, score, upcoming, activity } = data;
  return (
    <div data-testid="employer-overview">
      <OnboardModal />
      <PageHeader title={t("ovw.title")} subtitle={t("ovw.subtitle")} action={addBtn("overview-add-employee")} />

      {subcon && (() => {
        const crit = subcon.critical || 0, warn = subcon.warning || 0;
        const sev = crit > 0 ? "critical" : warn > 0 ? "warning" : "ok";
        const box = sev === "critical" ? "border-red-200 bg-red-50 hover:bg-red-100/70" : sev === "warning" ? "border-amber-200 bg-amber-50 hover:bg-amber-100/70" : "border-slate-200 bg-white hover:bg-slate-50";
        const ic = sev === "critical" ? "bg-red-100 text-red-600" : sev === "warning" ? "bg-amber-100 text-amber-600" : "bg-cyan/10 text-cyan-dark";
        const Icon = sev === "critical" ? AlertOctagon : sev === "warning" ? AlertTriangle : HardHat;
        return (
          <Link to="/app/employer/subcontractors/alerts" data-testid="subcon-alert-widget"
            className={`mb-6 flex items-center gap-4 rounded-2xl border p-5 transition-colors ${box}`}>
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${ic}`}><Icon className="h-5 w-5" /></span>
            <div className="flex-1">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("ovw.subconTitle")}</div>
              {sev === "ok" ? (
                <div className="mt-1 text-sm font-semibold text-navy">{t("ovw.subconOk")}</div>
              ) : (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {crit > 0 && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-2.5 py-1 text-xs font-semibold text-red-600" data-testid="subcon-critical-chip">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" /><span data-testid="subcon-critical-count">{crit}</span> {t("ovw.subconCritical")}
                    </span>
                  )}
                  {warn > 0 && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-white px-2.5 py-1 text-xs font-semibold text-amber-600" data-testid="subcon-warning-chip">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /><span data-testid="subcon-warning-count">{warn}</span> {t("ovw.subconWarning")}
                    </span>
                  )}
                </div>
              )}
            </div>
            <span className="flex items-center gap-1 text-sm font-medium text-cyan-dark">{t("ovw.subconView")} <ChevronRight className="h-4 w-4" /></span>
          </Link>
        );
      })()}

      {portal && (portal.internal.total > 0 || portal.subcontractor.total > 0) && (
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="portal-status-widget">
          <div className="mb-3 flex items-center gap-2">
            <IdCard className="h-4 w-4 text-cyan-dark" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("ovw.portalTitle")}</span>
          </div>
          <div className={`grid gap-4 ${portal.has_subcontractor_module ? "sm:grid-cols-2" : ""}`}>
            <PortalBar label={t("ovw.portalInternal")} b={portal.internal} t={t} testid="portal-internal" />
            {portal.has_subcontractor_module && <PortalBar label={t("ovw.portalSub")} b={portal.subcontractor} t={t} testid="portal-sub" />}
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("ovw.total")} value={data.total} icon={Users} accent="navy" testid="stat-total" />
        <StatCard label={t("ovw.compliant")} value={counts.COMPLIANT} icon={ShieldCheck} accent="green" testid="stat-compliant" />
        <StatCard label={t("ovw.action")} value={counts.ACTION_REQUIRED} icon={AlertTriangle} accent="amber" testid="stat-action" />
        <StatCard label={t("ovw.critical")} value={counts.CRITICAL + counts.INCOMPLETE} icon={AlertOctagon} accent="red" testid="stat-critical" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="card-soft p-6">
          <h3 className="font-heading text-lg font-bold text-navy">{t("ovw.scoreTitle")}</h3>
          <div className="mt-4 flex justify-center"><ScoreRing score={score} label={t("ovw.score")} /></div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {t("ovw.compliant")} {counts.COMPLIANT}</div>
            <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-amber-500" /> {t("ovw.action")} {counts.ACTION_REQUIRED}</div>
            <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-red-500" /> {t("ovw.critical")} {counts.CRITICAL}</div>
            <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-slate-400" /> {t("ovw.incomplete")} {counts.INCOMPLETE}</div>
          </div>
        </div>

        <div className="card-soft p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold text-navy">{t("ovw.upcoming")}</h3>
            <Link to="/app/employer/deadlines" className="text-sm text-cyan-dark hover:underline">{t("ovw.viewAll")}</Link>
          </div>
          <div className="mt-4 space-y-2">
            {upcoming.length === 0 ? <div className="py-8 text-center text-sm text-slate-400">{t("ovw.caughtUp")}</div> :
              upcoming.map((d, i) => (
                <Link key={i} to={`/app/employer/employees/${d.employee_id}`} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 hover:bg-slate-50">
                  <div>
                    <div className="text-sm font-semibold text-navy">{d.employee_name}</div>
                    <div className="text-xs text-slate-400">{d.type === "Passport" ? t("ovw.passport") : t("ovw.permit")}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-navy">{fmtDate(d.due_date)}</div>
                    <div className={`text-xs ${d.days_remaining <= 30 ? "text-red-500" : d.days_remaining <= 90 ? "text-amber-500" : "text-slate-400"}`}>{t("ovw.daysRemaining", { n: d.days_remaining })}</div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>

      <div className="mt-6 card-soft p-6" data-testid="emp-news-widget">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-navy"><Newspaper className="h-5 w-5 text-cyan-dark" /> {t("ai.newsTitle")}</h3>
          <Link to="/app/employer/compliance-news" className="text-sm text-cyan-dark hover:underline" data-testid="emp-news-viewall">{t("ovw.viewAll")}</Link>
        </div>
        <p className="mt-1 text-sm text-slate-500">{t("ai.newsSubEmp")}</p>
        <div className="mt-4"><ComplianceNewsList limit={5} /></div>
      </div>

      <div className="mt-6 card-soft p-6">
        <h3 className="font-heading text-lg font-bold text-navy">{t("ovw.activity")}</h3>
        <div className="mt-4 space-y-3">
          {activity.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">{t("ovw.noActivity")}</div> :
            activity.map((a) => (
              <div key={a.id} className="flex items-start gap-3 text-sm">
                <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500"><ActivityIcon className="h-3.5 w-3.5" /></div>
                <div className="flex-1">
                  <span className="font-medium text-navy">{a.action.replace(/[._]/g, " ")}</span>
                  {a.employee_name && <span className="text-slate-500"> · {a.employee_name}</span>}
                  <div className="text-xs text-slate-400">{a.actor_name} · {fmtDateTime(a.created_at)}</div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
